import type { ServerAction } from "@/types/domain";
import {
  PersonalAccessTokenScopeValues,
  type PersonalAccessToken,
  type PersonalAccessTokenScope,
} from "@features/personal-access-tokens/types";

const issueActionName = "personal-access-token:issue";
const admittedScopesMetadataKey = "token/scopes/admitted";
const knownScopes = new Set<string>(PersonalAccessTokenScopeValues);

/**
 * Возвращает только scope, явно перечисленные backend в метаданных выпуска.
 * `null` означает, что данные нельзя считать достоверным списком разрешений.
 */
export const readAdmittedScopes = (root: {
  actions?: ServerAction[];
}): readonly PersonalAccessTokenScope[] | null => {
  const issueActions = root.actions?.filter((action) => action.name === issueActionName) ?? [];
  if (issueActions.length !== 1) return null;

  const admitted = issueActions[0].metadata?.[admittedScopesMetadataKey];
  if (!Array.isArray(admitted) || !admitted.every((scope) => typeof scope === "string")) {
    return null;
  }

  const result = new Set<PersonalAccessTokenScope>();
  for (const scope of admitted) {
    if (knownScopes.has(scope)) result.add(scope as PersonalAccessTokenScope);
  }

  return [...result];
};

/**
 * Работоспособность определяется статусом и обоими отдельными флагами backend.
 */
export const classifyToken = (token: PersonalAccessToken): "working" | "not-working" =>
  token.status === "active" && !token.expired && !token.signedWithPreviousKey
    ? "working"
    : "not-working";
